/**
 * Shared presentational table for the two units views. Given already-fetched, normalized rows it
 * renders the scroll wrapper, header, body (skeleton / rows / load-more), fixed scrollbar, and the
 * shared "Add rent" and "Add tenants" modals. Data fetching lives in the container components
 * (UnitsTable and UnitsListTable); per-unit status and tenants are lazy-loaded by their own cells.
 */
import { useCallback, useRef, useState } from 'react';
import Link from 'next/link';

import { Anchor, Box, Button, Paragraph, Table, TextButton, VisuallyHidden } from '@zillow/constellation-10';

import { ModalTenantSelect } from '@zg-rentals/modal-tenant';
import { useQueryClient } from '@zg-rentals/react-query5';
import { RentalManagerUserPropertyWebAnalyticsWebApiHandler_get_queryOptions } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { getPhotoThumbUrl } from '@zg-rentals/zillow-rental-manager/utils';

import { usePortfolioFilters } from '../../../contexts/PortfolioFiltersContext';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useTableObserver } from '../../../hooks/queries/properties/useTableObserver';
import { usePrefetchMonthlyRentOnHover } from '../../../hooks/usePrefetchMonthlyRent';
import { useScreenSize } from '../../../hooks/useScreenSize';
import { cachePropertyAddress } from '../../../utils/navigationCacheUtils';
import numberUtils from '../../../utils/numberUtils';

import { getDashboardOverviewRoute, getListingRoute } from '../../../constants/urls';

import { useAuth } from '../../../components/AuthProvider';
import { FieldEditModal } from '../../../components/modals/FieldEditModal';
import MonthlyRentFormField from '../../../components/PostingPath/MonthlyRentFormField';
import { FixedScrollbar } from '../../../components/properties/FixedScrollbar';
import { PropertyPhoto } from '../../../components/PropertyPhoto';
import { TruncatedTextWithTooltip } from '../../../components/TruncatedTextWithTooltip';

import {
  hiddenMobileCellStyles,
  interactiveRowStyles,
  lastRowStyles,
  mobileAddressTextStyles,
  overflowMenuCellStyle,
  propertyRowLinkStyles,
  truncatedTextStyles,
  unitCellContentStyles,
  unitCellStyles,
  unitInfoStyles,
  unitThumbnailStyles,
} from '../../../styles/propertyCardTable';
import { propertyThumbnailStyles, tableCellContentWidths, unitsViewTableCellContentWidths } from '../styles';

import { emitPropertyCardClickEvent } from '../../../analytics/events/propertiesPage';
import { client } from '../../../http/zgApiClient';
import SkeletonUnitListRows from './SkeletonUnitListRows';
import SkeletonUnitRows from './SkeletonUnitRows';
import StatusCell from './StatusCell';
import TenantsCell from './TenantsCell';
import UnitOverflowMenu from './UnitOverflowMenu';

import type { SystemStyleObject } from '@/styled-system/types';
import type { ReactNode, SyntheticEvent } from 'react';
import type { ValueOf } from 'type-fest';
import type { ListingAlias, RentalManagerAddressDto } from '@zg-rentals/ts-rental-manager-api-client';

// Interactive cells (rent/tenants CTAs, popovers, overflow menu) shouldn't follow the row's link
// (preventDefault) or bubble up to other click handlers (stopPropagation).
const stopRowNavigation = (event: SyntheticEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

// Emits the property-card click analytics event when a unit row is navigated to.
const useUnitRowClickAnalytics = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useCallback(
    async (listingAlias: ListingAlias) => {
      try {
        const { lease: _lease, ...currentProperty } = await queryClient.fetchQuery(
          RentalManagerUserPropertyWebAnalyticsWebApiHandler_get_queryOptions({ listingAlias, includeLeases: true }),
        );

        emitPropertyCardClickEvent({ user, currentProperty });
      } catch {
        emitPropertyCardClickEvent({ user, currentProperty: { listingAlias } });
      }
    },
    [user, queryClient],
  );
};

// Normalized row consumed by the shared table; containers map their DTOs into this shape.
export type UnitTableRow = {
  listingAlias: ListingAlias;
  address: RentalManagerAddressDto;
  photoKey?: string;
  // Text shown in the Unit column.
  unitLabel: string;
  // Accessible name for the row link and thumbnail (may differ from the shown label).
  unitAccessibleLabel: string;
  // Secondary line, e.g. beds/baths/sqft.
  unitInfo: string;
  // Label passed to the overflow menu trigger.
  overflowLabel: string;
  // Raw unit identifier (e.g. "A", "1") used in confirmation dialogs.
  unitNumber?: string;
  rent?: number;
  isTotalMonthlyPrice?: boolean;
  isMultifamily?: boolean;
  // Address column lines; only rendered by the "list" variant.
  addressLines?: { streetLine?: string; cityStateZip: string };
};

type Column = {
  label: string;
  hideOnMobile?: boolean;
  hideLabel?: boolean;
  width?: SystemStyleObject;
  css?: SystemStyleObject;
};

// Only the Unit column + overflow menu are visible on mobile; the rest appear from lg up.
const MOBILE_VISIBLE_COLUMN_COUNT = 2;

const CARD_COLUMNS: Array<Column> = [
  { label: 'Unit', width: tableCellContentWidths.UNIT, css: unitCellStyles },
  { label: 'Rent', hideOnMobile: true, width: tableCellContentWidths.RENT },
  { label: 'Status', hideOnMobile: true, width: tableCellContentWidths.STATUS },
  { label: 'Tenants', hideOnMobile: true, width: tableCellContentWidths.TENANTS },
  { label: 'Overflow menu', hideLabel: true, width: tableCellContentWidths.OVERFLOW },
];

const LIST_COLUMNS: Array<Column> = [
  { label: 'Unit', width: unitsViewTableCellContentWidths.UNIT },
  { label: 'Address', hideOnMobile: true, width: unitsViewTableCellContentWidths.ADDRESS },
  { label: 'Rent', hideOnMobile: true, width: unitsViewTableCellContentWidths.RENT },
  { label: 'Status', hideOnMobile: true, width: unitsViewTableCellContentWidths.STATUS },
  { label: 'Tenants', hideOnMobile: true, width: unitsViewTableCellContentWidths.TENANTS },
  { label: 'Overflow menu', hideLabel: true, width: unitsViewTableCellContentWidths.OVERFLOW },
];

type VariantConfig = {
  testId: string;
  loadMoreTestId: string;
  columns: Array<Column>;
  unitCellCss?: SystemStyleObject;
  thumbnailStyles: SystemStyleObject;
  showAddressColumn: boolean;
  skeleton: ReactNode;
};

export const UNIT_TABLE_VARIANT = {
  CARD: 'card',
  LIST: 'list',
} as const;

export type UnitsTableVariant = ValueOf<typeof UNIT_TABLE_VARIANT>;

const VARIANT_CONFIG: Record<UnitsTableVariant, VariantConfig> = {
  [UNIT_TABLE_VARIANT.CARD]: {
    testId: 'UnitsTable',
    loadMoreTestId: 'UnitsTable-LoadMoreRow',
    columns: CARD_COLUMNS,
    unitCellCss: unitCellStyles,
    thumbnailStyles: unitThumbnailStyles,
    showAddressColumn: false,
    skeleton: <SkeletonUnitRows />,
  },
  [UNIT_TABLE_VARIANT.LIST]: {
    testId: 'UnitsListTable',
    loadMoreTestId: 'UnitsListTable-LoadMoreRow',
    columns: LIST_COLUMNS,
    thumbnailStyles: propertyThumbnailStyles,
    showAddressColumn: true,
    skeleton: <SkeletonUnitListRows />,
  },
};

type Props = {
  variant: UnitsTableVariant;
  rows: Array<UnitTableRow>;
  isLoading: boolean;
  isFetching?: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  onArchive?: (listingAlias: ListingAlias, address: RentalManagerAddressDto, unitNumber: string | undefined) => void;
  onEdit?: (listingAlias: ListingAlias) => void;
  // Scopes the rent cache invalidation to a group when known (per-card variant).
  addRentGroupId?: string;
  onTenantAdded?: () => void;
};

const UnitsTableView = ({
  variant,
  rows,
  isLoading,
  isFetching = false,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onArchive,
  onEdit,
  addRentGroupId,
  onTenantAdded,
}: Props) => {
  const config = VARIANT_CONFIG[variant];
  const { includeArchived } = usePortfolioFilters();
  const queryClient = useQueryClient();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // State, not a ref: the add-rent modal gates its save on this listing's details query.
  const [addRentListingAlias, setAddRentListingAlias] = useState<string | null>(null);
  const addTenantListingAliasRef = useRef<string | null>(null);
  const [displayAddRentModal, setDisplayAddRentModal] = useState(false);
  // Shares a cache entry with the rent field's own query, so this costs no extra request.
  const { data: addRentListingDetails, isLoading: isAddRentDetailsLoading } = useGetListingDetails(
    addRentListingAlias ?? '',
    { enabled: displayAddRentModal },
  );
  const canSaveAddRent = Boolean(addRentListingDetails?.floorPlans?.[0]?.id);
  const isEditingExistingRent = Boolean(rows.find((row) => row.listingAlias === addRentListingAlias)?.rent);
  const [displayAddTenantModal, setDisplayAddTenantModal] = useState(false);
  const getRentPrefetchProps = usePrefetchMonthlyRentOnHover();

  const { isScreenMobile } = useScreenSize();
  const { showFixedScrollbar, isTableBottomVisible } = useTableObserver({ scrollContainerRef, isScreenMobile });
  const handleRowClick = useUnitRowClickAnalytics();

  const loadMoreColSpan = isScreenMobile ? MOBILE_VISIBLE_COLUMN_COUNT : config.columns.length;
  const isStale = isFetching && !isFetchingNextPage;

  const handleRowNavigation = (event: SyntheticEvent, row: UnitTableRow) => {
    if (isStale) {
      stopRowNavigation(event);
      return;
    }

    // Card rows carry the group's address, so without the unit every one caches as the building.
    cachePropertyAddress(row.listingAlias, { ...row.address, unit: row.unitNumber || row.address.unit });
    handleRowClick(row.listingAlias);
  };

  const openRentEdit = (listingAlias: string) => {
    setAddRentListingAlias(listingAlias);
    setDisplayAddRentModal(true);
  };

  const openAddRent = (event: SyntheticEvent, listingAlias: string) => {
    stopRowNavigation(event);
    openRentEdit(listingAlias);
  };

  const openAddTenant = (listingAlias: string) => {
    addTenantListingAliasRef.current = listingAlias;
    setDisplayAddTenantModal(true);
  };

  const renderRow = (row: UnitTableRow, rowIndex: number) => {
    // When a load-more row follows, it becomes the visually last row instead.
    const isLastRow = rowIndex === rows.length - 1 && !hasNextPage;
    const route = row.isMultifamily
      ? getListingRoute({ listingAlias: row.listingAlias })
      : getDashboardOverviewRoute({ listingAlias: row.listingAlias });

    const tableRow = (
      <Table.Row
        interactive
        css={{
          ...interactiveRowStyles,
          ...(isLastRow ? lastRowStyles : {}),
        }}
      >
        {/* unit */}
        <Table.Cell css={config.unitCellCss}>
          <Box css={unitCellContentStyles}>
            <Box css={config.thumbnailStyles}>
              <PropertyPhoto src={getPhotoThumbUrl(row.photoKey)} alt={row.unitAccessibleLabel} />
            </Box>
            <Box css={unitInfoStyles}>
              <TruncatedTextWithTooltip text={row.unitLabel} textStyle="body-sm-bold" css={truncatedTextStyles} />
              <TruncatedTextWithTooltip
                text={row.unitInfo}
                textStyle="body-xs"
                color="text.subtle"
                css={truncatedTextStyles}
              />
              {config.showAddressColumn && row.addressLines?.streetLine && (
                <TruncatedTextWithTooltip
                  text={row.addressLines.streetLine}
                  textStyle="body-xs"
                  color="text.subtle"
                  css={mobileAddressTextStyles}
                />
              )}
            </Box>
          </Box>
        </Table.Cell>

        {/* address */}
        {config.showAddressColumn && (
          <Table.Cell css={hiddenMobileCellStyles}>
            <Box css={unitInfoStyles}>
              <TruncatedTextWithTooltip
                text={row.addressLines?.streetLine || '—'}
                textStyle="body-sm"
                css={truncatedTextStyles}
              />
              <TruncatedTextWithTooltip
                text={row.addressLines?.cityStateZip ?? ''}
                textStyle="body-xs"
                color="text.subtle"
                css={truncatedTextStyles}
              />
            </Box>
          </Table.Cell>
        )}

        {/* rent */}
        <Table.Cell css={hiddenMobileCellStyles}>
          {row.rent ? (
            <Paragraph textStyle="body-sm">
              {row.isTotalMonthlyPrice
                ? `${numberUtils.asCurrency(row.rent)} (Total monthly price)`
                : numberUtils.asCurrency(row.rent)}
            </Paragraph>
          ) : row.isMultifamily === true || includeArchived ? (
            <Paragraph textStyle="body-sm">—</Paragraph>
          ) : (
            <TextButton
              textStyle="body-sm"
              disabled={isStale}
              onClick={(event) => openAddRent(event, row.listingAlias)}
              {...getRentPrefetchProps(row.listingAlias)}
            >
              Add rent
            </TextButton>
          )}
        </Table.Cell>

        {/* status */}
        <Table.Cell css={hiddenMobileCellStyles}>
          <StatusCell listingAlias={row.listingAlias} />
        </Table.Cell>

        {/* tenants */}
        <Table.Cell css={hiddenMobileCellStyles}>
          <TenantsCell
            listingAlias={row.listingAlias}
            isMultifamily={row.isMultifamily}
            isAddTenantsDisabled={isStale}
            onAddTenants={() => openAddTenant(row.listingAlias)}
          />
        </Table.Cell>

        {/* overflow menu (stop the click here from following the row's link) */}
        <Table.Cell css={overflowMenuCellStyle} onClick={stopRowNavigation}>
          <UnitOverflowMenu
            listingAlias={row.listingAlias}
            unitNumber={row.overflowLabel}
            address={row.address}
            isDisabled={isStale}
            onArchiveClick={onArchive ? (la, addr) => onArchive(la, addr, row.unitNumber) : undefined}
            onEditClick={onEdit}
            onEditRentClick={() => openRentEdit(row.listingAlias)}
          />
        </Table.Cell>
      </Table.Row>
    );

    return (
      <Anchor key={row.listingAlias} asChild aria-label={row.unitAccessibleLabel} css={propertyRowLinkStyles}>
        <Link href={route} aria-disabled={isStale} onClick={(event) => handleRowNavigation(event, row)}>
          {tableRow}
        </Link>
      </Anchor>
    );
  };

  return (
    <>
      <Box css={{ overflow: { base: 'auto', lg: 'hidden' } }}>
        <Box ref={scrollContainerRef} css={{ overflowX: { base: 'hidden', lg: 'auto' }, maxWidth: '100%' }}>
          <Table.Root
            data-testid={config.testId}
            aria-label="Units"
            appearance="horizontal"
            css={{ width: '100%', tableLayout: 'fixed' }}
          >
            <Table.Header>
              <Table.Row>
                {config.columns.map((column) => (
                  <Table.HeaderCell
                    key={column.label}
                    css={{
                      ...(column.hideOnMobile ? hiddenMobileCellStyles : {}),
                      ...column.width,
                      ...column.css,
                    }}
                  >
                    {column.hideLabel ? (
                      <VisuallyHidden>{column.label}</VisuallyHidden>
                    ) : (
                      <Paragraph textStyle="body-sm-bold">{column.label}</Paragraph>
                    )}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {isLoading && config.skeleton}

              {!isLoading && rows.map(renderRow)}

              {!isLoading && hasNextPage && (
                <Table.Row data-testid={config.loadMoreTestId} css={lastRowStyles}>
                  <Table.Cell colSpan={loadMoreColSpan} css={{ padding: '400', textAlign: 'center' }}>
                    <Box css={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <Button
                        tone="brand"
                        emphasis="outlined"
                        size="sm"
                        onClick={onLoadMore}
                        loading={isFetchingNextPage}
                      >
                        Load more units
                      </Button>
                    </Box>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Root>
        </Box>

        <FixedScrollbar
          scrollContainerRef={scrollContainerRef}
          isVisible={showFixedScrollbar}
          isTableBottomVisible={isTableBottomVisible}
        />
      </Box>

      {displayAddRentModal && (
        <FieldEditModal
          title={isEditingExistingRent ? 'Edit rent' : 'Add rent'}
          saveLabel={isEditingExistingRent ? 'Save changes' : 'Add rent'}
          isOpen={displayAddRentModal}
          onClose={() => setDisplayAddRentModal(false)}
          isLoading={isAddRentDetailsLoading}
          isSaveDisabled={!isAddRentDetailsLoading && !canSaveAddRent}
        >
          <MonthlyRentFormField listingAlias={addRentListingAlias ?? undefined} groupId={addRentGroupId} />
        </FieldEditModal>
      )}

      {displayAddTenantModal && addTenantListingAliasRef.current && (
        <ModalTenantSelect
          client={client}
          listingAlias={addTenantListingAliasRef.current}
          isOpen={true}
          queryClient={queryClient}
          onAddRenterSuccess={() => {
            const alias = addTenantListingAliasRef.current;
            if (!alias) return;
            queryClient.invalidateQueries({
              predicate: ({ meta }) =>
                Boolean(meta?.tags?.includes('propertyTableTenants') && meta?.tags?.includes(alias)),
            });
            queryClient.invalidateQueries({
              predicate: ({ meta }) =>
                Boolean(meta?.tags?.includes('propertyTableStatus') && meta?.tags?.includes(alias)),
            });
            onTenantAdded?.();
          }}
          onClose={() => setDisplayAddTenantModal(false)}
        />
      )}
    </>
  );
};

export default UnitsTableView;
